<template>
  <div class="speaker-modal">
    <div class="speaker-modal__cont">
      <span class="speaker-modal__bg-red"></span>
      <div class="speaker-modal__container" data-cont="big">
        <div class="speaker-modal__item speaker-modal__carousel" ref="carousel">
          <SpeakerCarousel :currentIndex="currentIndex" :speakers="speakers"/>
        </div>

        <div class="speaker-modal__item speaker-modal__content">
          <transition @enter="enter" @leave="leave" mode="out-in">
            <router-view :data="speakers" :key="getSpeakerName"></router-view>
          </transition>
        </div>

        <div class="speaker-modal__carousel-controls" data-cont="big" :style="{ top: `${positionControls}px` }">
          <div class="carousel-controls__item u-hidden--until-ipadP">
            <BaseButtonFilled
              :class="['carousel-control__button', prevent ? 'carousel-control__button--prevent' : '']"
              :data="Object.assign(carouselControls[0], { url: `/${this.getCity}/speakers/`, rtl: true })"
              :symbol="'view-all'"
              :textHideM="true"
            />
          </div>

          <div class="carousel-controls__item" ref="controls">
            <BaseButtonFilled
              :class="['carousel-control__button', prevent ? 'carousel-control__button--prevent' : '']"
              :data="Object.assign(carouselControls[1], { url: prevSpeaker, rtl: true })"
              :textHideM="true"
            />
            <BaseButtonFilled
              :class="['u-hidden--from-ipadP', 'carousel-control__button', prevent ? 'carousel-control__button--prevent' : '']"
              :data="Object.assign(carouselControls[0], { url: `/${this.getCity}/speakers/` })"
              :textHideM="true"
              :symbol="'view-all'"
            />
            <BaseButtonFilled
              :class="['carousel-control__button', prevent ? 'carousel-control__button--prevent' : '']"
              :data="Object.assign(carouselControls[2], { url: nextSpeaker })"
              :textHideM="true"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import anime from 'animejs';
import { mapGetters } from 'vuex';
import loop from '@/scripts/loop';
import { qs } from '@/scripts/utils';

import * as Hammer from 'hammerjs';

import BaseButtonFilled from '@/components/BaseButtonFilled';
import SpeakerCarousel from '@/components/SpeakerCarousel';

export default {
  name: 'speakerModal',

  components: {
    BaseButtonFilled,
    SpeakerCarousel,
  },

  props: {
    speakers: {
      type: Array,
      required: true,
    },

    speakersPositions: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      hm: null,
      prevent: false,

      carouselControls: [
        {
          routerLink: true,
          url: `/${this.getCity}/speakers/`,
          content: 'View all',
        },
        {
          routerLink: true,
          url: this.prevSpeaker,
          content: 'prev',
        },
        {
          routerLink: true,
          url: this.nextSpeaker,
          content: 'next',
        },
      ],

      dir: 'next',

      positionControls: 0,
    };
  },

  computed: {
    ...mapGetters(['getCity', 'getSpeakerName']),

    currentIndex() {
      const getIndex = this.speakers.findIndex(el => el.path === `/${this.getSpeakerName}`);
      const currentIndex = getIndex === -1 ? 0 : getIndex;

      return currentIndex;
    },

    prevSpeaker() {
      const prevIndex = this.currentIndex === 0 ? (this.speakers.length - 1) : (this.currentIndex - 1);

      return `/${this.getCity}/speakers${this.speakers[prevIndex].path}`;
    },

    nextSpeaker() {
      const nextIndex = this.currentIndex === (this.speakers.length - 1) ? 0 : (this.currentIndex + 1);

      return `/${this.getCity}/speakers${this.speakers[nextIndex].path}`;
    },
  },

  watch: {
    getSpeakerName(newVal) {
      if (newVal !== undefined) {
        this.$eventHub.$emit('scrollTo', this.speakersPositions[this.currentIndex]);
      }
    },

    currentIndex(newVal, oldVal) {
      this.dir = newVal > oldVal ? newVal === this.speakers.length -1 && oldVal === 0 ? 'prev' : 'next' : newVal === 0 && oldVal === this.speakers.length -1 ? 'next' :'prev';
    },
  },

  mounted() {
    document.body.setAttribute('js-headervisible', '');

    window.scroll(0, 0);

    this.hm = new Hammer(this.$refs.carousel);
    this.bindHammer();

    this.checkPosition();
    loop.add(this.checkPosition, 'checkScrollSpeaker');
  },


  methods: {
    checkPosition() {
      this.positionControls = window.innerHeight - this.$refs.controls.getBoundingClientRect().height - 40 + this.$el.scrollTop;
    },

    enter(el, done) {
      const tX = this.dir === 'prev' ? -1 : 1;
      const title = qs('h3', el);
      const subtitle = qs('h4', el);
      const paragraph = qs('p', el);
      const list = qs('ul', el);

      anime({
        targets: title,
        duration: 800,
        opacity: [0, 1],
        translateX: [tX * 100, 0],
        easing: [0.25, 0.87, 0.43, 1],
      });
      anime({
        targets: subtitle,
        duration: 800,
        opacity: [0, 1],
        translateX: [tX * 100, 0],
        easing: [0.25, 0.87, 0.43, 1],
        delay: 50,
      });
      anime({
        targets: paragraph,
        duration: 800,
        opacity: [0, 1],
        translateX: [tX * 100, 0],
        easing: [0.25, 0.87, 0.43, 1],
        delay: 100,
      });
      anime({
        targets: list,
        duration: 800,
        opacity: [0, 1],
        translateX: [tX * 100, 0],
        easing: [0.25, 0.87, 0.43, 1],
        delay: 150,
        complete: () => {
          this.prevent = false;
          document.body.classList.remove('wait');
          done();
        },
      });
    },

    leave(el, done) {
      const tX = this.dir === 'prev' ? 1 : -1;
      this.prevent = true;

      const title = qs('h3', el);
      const subtitle = qs('h4', el);
      const paragraph = qs('p', el);
      const list = qs('ul', el);

      anime({
        targets: title,
        duration: 600,
        opacity: [1, 0],
        translateX: [0, tX * 100],
        easing: 'easeInOutSine',
      });
      anime({
        targets: subtitle,
        duration: 600,
        opacity: [1, 0],
        translateX: [0, tX * 100],
        easing: 'easeInOutSine',
        delay: 50,
      });
      anime({
        targets: paragraph,
        duration: 600,
        opacity: [1, 0],
        translateX: [0, tX * 100],
        easing: 'easeInOutSine',
        delay: 100,
      });
      anime({
        targets: list,
        duration: 600,
        opacity: [1, 0],
        translateX: [0, tX * 100],
        easing: 'easeInOutSine',
        delay: 150,
        complete: done,
      });
    },

    bindHammer() {
      this.hm.on('swipeleft', this.swipeLeft);
    },

    swipeLeft() {
      this.$router.push(this.nextSpeaker);
    },

    swipeRight() {
      this.$router.push(this.prevSpeaker);
    },

    bindHammer() {
      if (this.isMobile) {
        this.hm.on('swipeleft', () => {
          this.swipeDirection = 'left';
          this.clickRandom();
        });
        this.hm.on('swiperight', () => {
          this.swipeDirection = 'right';
          this.clickRandom();
        });
      } else {
        this.hm.off('swipeleft');
        this.hm.off('swiperight');
      }
    },
  },

  beforeDestroy() {
    document.body.removeAttribute('js-headervisible');
    this.hm.off('swipeleft', this.swipeLeft);
    this.hm.off('swiperight', this.swipeRight);

    loop.remove('checkScrollSpeaker');
  },
};
</script>

<style lang="scss" scoped>
.speaker-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  height: calc(var(--vh, 1vh) * 100);

  background-color: $color-bg;

  z-index: z-index(modal);
  overflow: scroll;
  -webkit-overflow-scrolling: touch;

  @include mq(ipadP) {
    height: 100vh;
    height: calc(var(--vh, 1vh) * 100);
    overflow: hidden;
  }

  &__bg-red {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 50%;

    background-color: $color-red;

    @include mq(ipadP) {
      width: 50%;
      height: 100%;
    }
  }

  &__container {
    position: relative;
    width: 100%;

    display: flex;
    flex-flow: column wrap;
    align-items: flex-start;
    justify-content: flex-start;

    @include mq(ipadP) {
      height: 100vh;
      height: calc(var(--vh, 1vh) * 100);

      flex-flow: row wrap;
      align-items: stretch;
    }
  }

  &__item {
    position: relative;

    padding: 120px 20px 30px;

    @include mq(mobile) {
      padding: 120px 30px 30px;
    }

    @include mq(ipadP) {
      padding: 120px 40px 0;
    }

    @include mq(ipadP) {
      padding: 0 40px;
    }

    @include mq(ipadL) {
      padding: 0 80px;
    }

    @include mq(ds) {
      padding: 0 120px;
    }

    display: flex;
    align-items: center;
    justify-content: center;

    @include mq(ipadP) {
      justify-content: flex-start;
    }

    &:nth-child(2) {
      background-color: $color-bg;
      overflow: hidden;

      padding: 10px 20px 0;

      @include mq(mobile) {
        padding: 10px 30px 0;
      }

      @include mq(mobileL) {
        padding: 60px 80px 0;
      }

      @include mq(ipadP) {
        padding: 0 40px;
      }

      @include mq(ipadL) {
        padding: 0 80px;
      }

      @include mq(ds) {
        padding: 0 120px;
      }
    }
  }

  &__carousel {
    width: 100%;
    flex: 0 0 100%;
    overflow: hidden;

    @include mq(ipadP) {
      width: 45%;
      flex: 0 0 45%;
      overflow: auto;
    }
  }

  &__content {
    width: 100%;
    flex: 0 0 100%;

    @include mq(ipadP) {
      width: 55%;
      flex: 0 0 55%;
    }
  }

  &__carousel-controls {
    position: fixed;
    width: 100%;
    height: auto;
    background-color: rgba(242, 242, 242, 1);

    display: flex;
    align-items: center;
    justify-content: flex-start;

    padding: 20px 30px;

    @include mq(ipadP) {
      padding: 20px 40px;
      background-color: transparent;
    }

    @include mq(ipadL) {
      padding: 20px 80px;
    }

    @include mq(ds) {
      padding: 20px 0;
    }

    .carousel-controls__item {
      position: relative;
      width: 0;

      @include mq(ipadP) {
        flex: 0 0 45%;
        width: 45%;
      }

      &:nth-child(2) {
        width: 100%;
        flex: 0 0 100%;
        padding: 0;

        display: flex;
        align-items: center;
        justify-content: space-between;

        @include mq(ipadP) {
          width: 55%;
          flex: 0 0 55%;
          padding: 0 0 0 40px;
        }

        @include mq(ipadL) {
          padding: 0 0 0 80px;
        }

        @include mq(ds) {
          padding: 0 0 0 120px;
        }
      }
    }

    .carousel-control__button {
      pointer-events: auto;

      &.carousel-control__button--prevent {
        pointer-events: none;
      }
    }
  }
}
</style>
